import {
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from '@mantine/core';

/**
 * The status scales, and why they are ours rather than Mantine's.
 *
 * A component that says `color="yellow"` is not naming a colour, it is naming a *palette*, and
 * Mantine derives three things from it that all have to be read: `variant="light"` puts shade 9 on
 * shade 1, `variant="filled"` puts white on shade 6, and the dark scheme puts shade 0 on a tint of
 * the same hue. Measured against WCAG 1.4.3, Mantine's own palettes fail the first two for every
 * warm and green hue there is: `yellow-9` on `yellow-1` is 2.68:1, `teal-9` on `teal-1` is 4.32:1,
 * `green-9` on `green-1` is 3.80:1, `orange-9` on `orange-1` is 3.61:1 — and no darker shade
 * exists, so this cannot be fixed by picking a different number. White on `red-6` is 3.28:1, which
 * is every destructive button in the product.
 *
 * So the product declares five semantic scales instead. Shades 0–5 are Mantine's — they are tints
 * and hover states, and nothing reads text off them. Shades 6–9 are the same hue darkened by a
 * single factor per scale, chosen as the lightest one that clears the two numbers with margin:
 *
 * | Scale | Hue | Factor | shade 9 on shade 1 | white on shade 6 | shade 0 on shade 9 |
 * |---|---|---|---|---|---|
 * | `danger` | red | 0.80 | 6.26:1 | 4.86:1 | 7.09:1 |
 * | `warning` | yellow | 0.60 | 6.31:1 | 4.81:1 | 6.64:1 |
 * | `success` | green | 0.70 | 6.53:1 | 4.57:1 | 6.98:1 |
 * | `info` | blue | 0.85 | 6.21:1 | 4.72:1 | 6.90:1 |
 * | `neutral` | gray | 0.80 | 15.08:1 | 4.87:1 | 15.91:1 |
 *
 * The numbers are not maintained by hand: `test/theme/tokens.test.ts` recomputes the pairs from
 * these arrays, in both schemes, on every run — the same treatment `BRAND_COLORS` already gets.
 *
 * Naming them by *status* rather than by hue is the half of this that a linter can hold: hue names
 * are what let `color="yellow"` mean «amber, and hope», and `lawyer-broad-profile/no-raw-mantine-color` now
 * refuses them. A screen asks for `danger`, and which colour that is stays one decision.
 */
export const DANGER_COLORS: MantineColorsTuple = [
  '#fff5f5',
  '#ffe3e3',
  '#ffc9c9',
  '#ffa8a8',
  '#ff8787',
  '#ff6b6b',
  '#c84242',
  '#c03232',
  '#b32727',
  '#a12222',
];

export const WARNING_COLORS: MantineColorsTuple = [
  '#fff9db',
  '#fff3bf',
  '#ffec99',
  '#ffe066',
  '#ffd43b',
  '#fcc419',
  '#966a03',
  '#935f00',
  '#905400',
  '#8a4700',
];

export const SUCCESS_COLORS: MantineColorsTuple = [
  '#ebfbee',
  '#d3f9d8',
  '#b2f2bb',
  '#8ce99a',
  '#69db7c',
  '#51cf66',
  '#2d863d',
  '#277d36',
  '#216f30',
  '#1e612b',
];

export const INFO_COLORS: MantineColorsTuple = [
  '#e7f5ff',
  '#d0ebff',
  '#a5d8ff',
  '#74c0fc',
  '#4dabf7',
  '#339af0',
  '#1d76c4',
  '#186bb6',
  '#1560a5',
  '#145591',
];

export const NEUTRAL_COLORS: MantineColorsTuple = [
  '#f8f9fa',
  '#f1f3f5',
  '#e9ecef',
  '#dee2e6',
  '#ced4da',
  '#adb5bd',
  '#6b7278',
  '#3a4046',
  '#2a2e33',
  '#1a1e21',
];

/**
 * The brand scale, ten shades, dark enough to carry white text.
 *
 * Mantine's own blue fails WCAG AA as a filled button background — `#228be6` under white text is
 * about 3.1:1, and a primary action is body-sized text, so it needs 4.5:1. This scale is measured
 * instead of chosen: shade 6 (light scheme) is 4.63:1 under white and shade 8 (dark scheme) is
 * 7.08:1, and `test/theme/tokens.test.ts` recomputes both from this array on every run.
 */
export const BRAND_COLORS: MantineColorsTuple = [
  '#eef3ff',
  '#dce4f5',
  '#b9c7e2',
  '#94a8d0',
  '#748dc1',
  '#5f7cb8',
  '#5474b4',
  '#44639f',
  '#39588f',
  '#2d4b81',
];

/**
 * Поверхность всплывающего слоя — модалки и дровера, одним объявлением на оба.
 *
 * Почему это нужно объявлять отдельно и почему именно эти пять свойств — в комментарии у
 * `components.ModalContent` ниже: Mantine строит тело оверлея из `Paper`, и без этого оверлей
 * молча одевается карточкой.
 */
const OVERLAY_SURFACE = {
  bg: 'var(--lw-surface-overlay)',
  bd: '1px solid var(--lw-border-overlay)',
  p: 0,
  shadow: 'xl',
  withBorder: false,
} as const;

/**
 * Заголовок оверлея — без собственной заливки: он такая же часть панели, как её тело.
 *
 * Это не «убрали фон»: заголовок `sticky`, и прозрачный фон означал бы содержимое, проезжающее под
 * ним при прокрутке. Он берёт **тот же** токен, что и тело, — то есть перестаёт быть отдельной
 * поверхностью, оставаясь непрозрачным.
 */
const OVERLAY_HEADER = { bg: 'var(--lw-surface-overlay)' } as const;

/**
 * The theme is a *narrowing* of Mantine, not a replacement (ADR-0006): what it fixes is the small
 * set of choices that must not be made per component — the brand scale, the radius vocabulary, the
 * type scale — so that a screen assembled by two people still looks like one product.
 *
 * `primaryShade` differs per scheme on purpose. One shade cannot satisfy both: light enough to read
 * against a dark surface is too light to carry white text on a button, and the compromise is the
 * button nobody can read.
 */
export const appTheme: MantineThemeOverride = createTheme({
  colors: {
    brand: BRAND_COLORS,
    danger: DANGER_COLORS,
    warning: WARNING_COLORS,
    success: SUCCESS_COLORS,
    info: INFO_COLORS,
    neutral: NEUTRAL_COLORS,
  },
  primaryColor: 'brand',
  primaryShade: { light: 6, dark: 8 },

  /** Controls `sm`, cards `md`, overlays `lg` — the default is the one controls use. */
  defaultRadius: 'sm',

  /**
   * Honour `prefers-reduced-motion`. Mantine 9 defaults this to `false`, so without the line the
   * setting a person turned on in their operating system — often because motion makes them ill —
   * reaches our modals, drawers and transitions and is ignored by all of them.
   *
   * It also removes a whole class of false accessibility failure. An axe audit taken while a dialog
   * is fading in measures the *composite* of the element over what is behind it, and reports pairs
   * like `#ecd0d0` on `#b65858` — colours present in no palette and in no stylesheet, drifting run
   * to run with wherever the transition happened to be. One of those survived the entire palette
   * being repaired and looked exactly like a contrast defect. With the preference respected, a
   * violation reported by the suite is a fact about the tokens rather than about the frame the
   * audit landed on.
   */
  respectReducedMotion: true,

  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',

  /**
   * 1.5 is a WCAG 1.4.12 floor for body text, not a taste: below it, text at 200 % zoom overlaps.
   * Headings are allowed to be tighter because they are large text.
   */
  lineHeights: { xs: '1.5', sm: '1.5', md: '1.55', lg: '1.55', xl: '1.6' },

  headings: {
    fontWeight: '600',
    sizes: {
      /*
       * Кегли — из собственной шкалы `--lw-font-size-h*`, а не из общей шкалы размеров.
       *
       * Прежняя редакция брала `--mantine-font-size-*`, и `h3` с `h4` получали **одно и то же**
       * значение `md`. Комментарий рядом утверждал, что уровни заведены ради того, чтобы иерархия
       * «не схлопывалась в один кегль», — она схлопывалась ровно здесь: `Section` рисует `h2`
       * размером `h3`, заголовок карточки — `h4`, и оба выходили в 16px, то есть весь экран, кроме
       * заголовка страницы, был набран одним размером. Различал уровни только вес.
       */
      h1: { fontSize: 'var(--lw-font-size-h1)', lineHeight: '1.25' },
      h2: { fontSize: 'var(--lw-font-size-h2)', lineHeight: '1.3' },
      h3: { fontSize: 'var(--lw-font-size-h3)', lineHeight: '1.4' },
      h4: { fontSize: 'var(--lw-font-size-h4)', lineHeight: '1.5' },
      h5: { fontSize: 'var(--lw-font-size-h5)', lineHeight: '1.5' },
    },
  },

  components: {
    /**
     * Заметка **говорит, а не кричит** — и это объявлено один раз здесь, как вид карточки ниже.
     *
     * `Alert` Mantine роль по умолчанию имеет: `role: role || "alert"`
     * (`@mantine/core@9.5.1/esm/components/Alert/Alert.mjs:51`). Панель, роль которой никто не
     * задал, оказывается `assertive` — она перебивает чтение, а неявный `aria-atomic` заставляет
     * перечитывать её целиком при каждом изменении содержимого.
     *
     * Проверка доступности 30.08.2026 нашла **десять** таких панелей, и все — статические заметки,
     * а не тревоги: «документ выведен из обращения», «вы смотрите на прошлую дату», «прогон можно
     * показывать как пройденный», «сверка выключена в этой установке». Юрист, читающий документ на
     * слух, получал объявление посреди пункта. Сказать это надо — перепутанная редакция есть
     * неверная правовая позиция, — но очередь `polite` дочитает фразу и произнесёт заметку следом,
     * ничего не потеряв.
     *
     * Двум из тех десяти док-строка при этом сообщала, что «`Alert` Mantine несёт свою роль».
     * Несёт — и это `alert`; посылка была верна по букве и обратна по смыслу.
     *
     * Осознанная тревога задаётся явно и это умолчание перекрывает: состояние ошибки
     * (`shared/ui/data-state/error-state.component.tsx`), отказ провайдера, отклонённое вложение —
     * шесть мест в дереве, и все они роль указывают.
     */
    Alert: {
      defaultProps: { role: 'status' },
    },

    /**
     * Карточка — `Paper`, и её вид объявлен один раз здесь.
     *
     * До этого три юнита (`ai-provider`, `document`, `assistant`) держали в своих CSS-модулях
     * побайтово одинаковый класс `.card`: одна и та же поверхность, граница, радиус и отступ. Четыре
     * копии одного решения расходятся молча — первая же правка одной из них делает экраны разными,
     * и никто этого не замечает, потому что каждая копия сама по себе верна.
     */
    Paper: {
      defaultProps: {
        withBorder: true,
        p: 'md',
        radius: 'md',
        bg: 'var(--lw-surface-raised)',
        /*
         * Карточка в покое приподнята на первую ступень.
         *
         * До этого во всём клиенте не было ни одной тени: `grep -rn "shadow" src` не давал ни
         * одного совпадения, и карточка отличалась от фона заливкой на 1.05:1 плюс границей 1.30:1,
         * то есть почти ничем. Ступень задаётся **именем размера**, а не `var(--lw-elevation-1)`:
         * Mantine превращает проп в `var(--mantine-shadow-<значение>)`, и переменная внутри пропа
         * скомпилировалась бы в мусор, молча ничего не нарисовав.
         */
        shadow: 'xs',
      },
    },

    /*
     * Всплывающий слой — своя поверхность, а не карточка. Объявляется здесь по той же причине, по
     * которой здесь объявлена карточка: иначе это решение принимает каждая модалка отдельно.
     *
     * `ModalContent`/`DrawerContent` — это `Paper` (`ModalBaseContent` рендерит именно его), поэтому
     * `Paper.defaultProps` выше доставались и оверлею. Ни одна модалка в `src` не передаёт ни
     * `classNames`, ни `styles`, ни `headerProps` — вид оверлея никто не выбирал, он получался.
     * Стоит это трёх расхождений сразу, и все три чинятся одной таблицей:
     *
     * 1. **заливка.** Тело брало `--lw-surface-raised` (карточка), а собственный заголовок Mantine
     *    рисует `--mantine-color-body` — фоном страницы. В тёмной схеме это `dark-6` против
     *    `dark-7`: заголовок **темнее** тела, то есть та самая «тёмная подложка», про которую
     *    заказчик спросил «зачем она вообще нужна». В светлой схеме обе величины белые, поэтому
     *    дефект не был виден ни на одном светлом скриншоте;
     * 2. **отступ.** `p: md` — отступ карточки; у заголовка и тела оверлея уже есть свой
     *    (`--mb-padding`), и вместе они давали 32px от края до заголовка — плашка читалась как
     *    вставленная внутрь панель. Отступ оверлея задаёт оверлей, отсюда `p: 0`;
     * 3. **токен.** У всплывающего слоя есть собственные токены — `--lw-surface-overlay`,
     *    `--lw-border-overlay` и радиус `lg` («Controls `sm`, cards `md`, overlays `lg`» из
     *    `defaultRadius` выше). Их не потреблял никто, хотя `test/theme/tokens.test.ts` меряет
     *    контраст текста, границ и фокус-кольца именно по `--lw-surface-overlay`: тест обещал одно,
     *    экран красил другим.
     *
     * `bd` вместо `withBorder`: граница нужна — в тёмной схеме именно край, а не тень, говорит «этот
     * слой плавает» (`tokens.css`), — но `withBorder` рисует её цветом контрола
     * (`--mantine-color-default-border`), а не краем оверлея.
     *
     * Заголовку заливка назначается **пропом, а не классом**, и это не стиль, а механика: свой фон
     * Mantine объявляет обычным классом, и CSS-модуль поверх него зависел бы от порядка подключения
     * стилей — ровно та хрупкость, которая описана у `--mantine-color-default-border` в
     * `tokens.css`. Стиль-проп приземляется инлайном и порядку не подчиняется.
     */
    ModalContent: { defaultProps: { ...OVERLAY_SURFACE, radius: 'lg' } },
    // Без `radius`: `DrawerContent` вынимает его из пропсов и до `Paper` он не доезжает, а дровер
    // прижат к краю экрана — скруглять ему нечего. Проп, который молча никуда не идёт, хуже
    // отсутствующего.
    DrawerContent: { defaultProps: OVERLAY_SURFACE },
    ModalHeader: { defaultProps: OVERLAY_HEADER },
    DrawerHeader: { defaultProps: OVERLAY_HEADER },

    /**
     * Every icon-only control is a `Tooltip` candidate and every tooltip in this product is
     * keyboard-reachable, so the delay is short enough to feel like a label rather than a reward
     * for hovering (`rules/a11y.mdc` §17 — the tooltip is *not* the accessible name, the
     * `aria-label` is; this only stops the two from disagreeing about when they appear).
     */
    Tooltip: { defaultProps: { openDelay: 200, withArrow: true } },
  },
});

/**
 * The Mantine variables this product replaces, as opposed to the ones it consumes.
 *
 * `--mantine-color-dimmed` is what Mantine's own components put on the description under an input,
 * a checkbox or a table caption. No file in this repository writes that text, so renaming
 * `c="dimmed"` at the call sites fixes the muted text we author and leaves the muted text the
 * library renders — which is most of it, and which is what `@axe-core/playwright` reported on
 * `/settings/security`. Measured, the default is `gray-6` on white at 3.32:1 and `dark-2` on
 * `dark-7` at 4.03:1: secondary body text below the 4.5:1 of WCAG 1.4.3, in **both** schemes.
 *
 * Done here rather than by re-declaring the variable in `tokens.css`, and the difference is not
 * stylistic. `MantineProvider` renders its resolved variables into a `<style>` inside the tree —
 * after `<head>` in document order, at the same specificity — so a stylesheet override only wins
 * while Mantine's deduplication happens to strip that variable for being equal to its default.
 * Change the value through this resolver and the variable stops being equal to its default, which
 * is precisely when the stylesheet override would have lost. One mechanism, and it is the one that
 * cannot be undone by load order.
 */
export const appCssVariables: CSSVariablesResolver = () => ({
  variables: {
    /*
     * Шкала теней Mantine отображается на ступени высоты продукта — одной точкой, а не правкой
     * компонентов.
     *
     * Своими тенями Mantine рисует `Modal`, `Drawer`, `Notification` и ползунок `Slider`, и её
     * значения — литеральные `rgba(0, 0, 0, …)`, **одинаковые в обеих схемах**. На тёмном фоне их
     * не видно вовсе. Отображение делает их зависимыми от темы, не трогая ни один компонент.
     *
     * Здесь, в `variables`, а не в `light`/`dark`: `--lw-elevation-*` переключаются сами.
     */
    '--mantine-shadow-xs': 'var(--lw-elevation-1)',
    '--mantine-shadow-sm': 'var(--lw-elevation-1)',
    '--mantine-shadow-md': 'var(--lw-elevation-2)',
    '--mantine-shadow-lg': 'var(--lw-elevation-3)',
    '--mantine-shadow-xl': 'var(--lw-elevation-3)',
  },
  light: { '--mantine-color-dimmed': 'var(--lw-text-muted)' },
  dark: { '--mantine-color-dimmed': 'var(--lw-text-muted)' },
});
